import { DOCUMENT } from "@angular/common";
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Directive,
  Component,
  ElementRef,
  HostListener,
  Inject,
  Input,
  OnDestroy,
  Renderer2,
  ViewChild,
  Output,
  HostBinding,
  EventEmitter,
  ViewEncapsulation,
  ContentChild,
} from "@angular/core";
import { BooleanInput, coerceBooleanProperty, toPx, uuid, Debounce, isMobile } from "@vattenfall/util";

/** The trigger of the tooltip element */
export type TooltipTrigger = "auto" | "click" | "hover" | "focus" | "custom";

/** The color of the tooltip element */
export type TooltipColor = "blue" | "yellow" | "black" | "white";

/** The position of the tooltip element */
export type TooltipPosition = "top" | "right" | "bottom" | "left";

/** The coordinates for a tooltip position variant */
export type TooltipContext = {
  /** The screen estate available from the anchor position to the viewport anchor position */
  readonly availableScreenEstate: number;
  /** The position of the host element from which the tooltip will be anchored to (for example a tooltip positioned to the right will be anchored to the left of the host element) */
  readonly anchorPosition: TooltipPosition;
  /** Horizontal position of host element relative to anchor position */
  readonly xPosition: ["left" | "right", number];
  /** Vertical position of host element relative to anchor position */
  readonly yPosition: ["top" | "bottom", number];
};

/** Hash of the coordinates for each position variation */
export type TooltipContexts = {
  readonly [key in TooltipPosition]: TooltipContext;
};

/**
 * The tooltip label element
 */
@Directive({
  selector: "ui-tooltip-label",
})
export class TooltipLabelDirective {
  constructor(public elementRef: ElementRef<HTMLElement>) {}
}

/**
 * Vattenfall UI `ui-tooltip` display informative text when users clicks, hover over, focus on, or tap an element.
 *
 * Pass in the element which the tooltip will be anchored to. A position (such as top or right) of the tooltip can be specified, but if there is not sufficient screen estate, it will be moved to a better position.
 *
 * The tooltip must have a label. You can provide the label as an input on the `<ui-tooltip>` element or by providing it inside an <ui-tooltip-label> element.
 */
@Component({
  selector: "ui-tooltip, [ui-tooltip]",
  templateUrl: "./tooltip.component.html",
  styleUrls: ["./tooltip.component.scss"],
  encapsulation: ViewEncapsulation.Emulated,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TooltipComponent implements AfterViewInit, OnDestroy {
  /**
   * Whether the dialog should be opened or closed.
   *
   * Default is `false`.
   */
  @Input()
  get open(): boolean {
    return this._open;
  }
  set open(value: BooleanInput) {
    this._open = coerceBooleanProperty(value);
    if (this.tooltipElement) this.setTooltipPosition();
  }
  private _open = false;

  /**
   * Event emitted every time the tooltip is enabled or disabled by the user.
   */
  @Output() readonly openChange = new EventEmitter<boolean>();

  /**
   * Defines the preferred position of the tooltip label. Will change to a different position is the available screen estate is not enough to display the label.
   *
   * Default is `top`.
   */
  @Input()
  get position(): TooltipPosition {
    return this._position;
  }
  set position(value: TooltipPosition) {
    // Ensure assigned value is not null or undefined
    this._position = value ?? this._position;
  }
  private _position: TooltipPosition = "top";

  /**
   * Defines the trigger for the tooltip.
   *
   * **auto**
   * Tooltip appears when you hover over the target or when the target element receives the focus. Will trigger on click (or tap) on mobile devices.
   *
   * **click**
   * Tooltip appears when you click a target element.
   *
   * **hover**
   * Tooltip appears when you hover over the target. Will trigger on click (or tap) on mobile devices.
   *
   * **focus**
   * Tooltip appears when you focus (say through tab key) on a target element. Will trigger on click (or tap) on mobile devices.
   *
   * **custom**
   * Tooltip is not triggered by any default action. So, you have to bind your own events and use the `open` input property.
   *
   * Default is `click`.
   */
  @Input()
  get trigger(): TooltipTrigger {
    return this._trigger;
  }
  set trigger(value: TooltipTrigger) {
    // Should trigger on click (or tap) on mobile devices
    this._trigger = isMobile ? "click" : value;
  }
  private _trigger: TooltipTrigger = "click";

  /**
   * Label of target element shown inside tooltip.
   *
   * May also be projected using the <ui-tooltip-label>` element, if both are used then the input will have higher priority.
   *
   * Note that the title is static and can only be set once (on initialization).
   */
  @Input()
  get label(): string {
    return this._label;
  }
  set label(value: string | undefined) {
    this._label = value ?? "";
  }
  private _label = "";

  /**
   * Defines the background color for the tooltip label.
   *
   * Default is `blue`.
   */
  @Input() color: TooltipColor = "blue";

  constructor(
    @Inject(DOCUMENT) private document: Document,
    private renderer: Renderer2,
    private elementRef: ElementRef<HTMLElement>
  ) {}

  /** Toggle tooltip when host element is clicked, taped or activated using keyboard */
  @HostListener("click", ["$event"])
  @HostListener("keydown.space", ["$event"])
  @HostListener("keydown.escape", ["$event"])
  onClick(event: KeyboardEvent) {
    if (this._isClick) {
      if (event.type === "click") this.setOpen(!this.open);
      if (event.type === "keydown") {
        if (event.code === "Space") this.setOpen(!this.open);
        if (event.code === "Escape") this.setOpen(false);
      }
    }
  }

  /** Toggle tooltip when host element is hovered */
  @HostListener("mouseenter", ["$event"])
  @HostListener("mouseleave", ["$event"])
  onHover(event: PointerEvent) {
    if (this._isHover) {
      if (event.type === "mouseenter") this.setOpen(true);
      if (event.type === "mouseleave") this.setOpen(false);
    }
  }

  /** Toggle tooltip when host element is activated */
  @HostListener("focus", ["$event"])
  @HostListener("blur", ["$event"])
  onFocus(event: PointerEvent) {
    if (this._isFocus) {
      if (event.type === "focus") this.setOpen(true);
      if (event.type === "blur") this.setOpen(false);
    }
  }

  /** Close tooltip when `click` event is fired outside of the tooltip */
  @HostListener("document:click", ["$event"])
  onDocumentClick(event: PointerEvent) {
    // Only run when tooltip is open to avoid unnecessary calculations (his event is triggered every time the user clicks).
    if (this.open && this._isClick) {
      // Remove last item of event path since it's a 'Window' object (and not a 'Node').
      const composedPath = event.composedPath().slice(0, -1) as Array<Node>;
      const isOutsideTooltip = !composedPath.some((node) => this.elementRef.nativeElement.isSameNode(node));
      if (isOutsideTooltip) this.setOpen(false);
    }
  }

  /** Re-calculate the position when the screen is adjusted */
  @HostListener("window:resize")
  @Debounce(50)
  onResize() {
    this.setTooltipPosition();
  }

  @HostBinding("attr.aria-describedby") uuid = uuid();

  @ViewChild("tooltipElement", { read: ElementRef }) private tooltipElement!: ElementRef<HTMLDivElement>;
  @ViewChild("tooltipLabelElement", { read: ElementRef }) private tooltipLabelElement!: ElementRef<HTMLDivElement>;
  @ViewChild("tooltipArrowElement", { read: ElementRef }) private tooltipArrowElement!: ElementRef<HTMLDivElement>;

  @ContentChild(TooltipLabelDirective) private tooltipLabelDirective!: TooltipLabelDirective;

  private get _isClipped() {
    const sourceElementRect = this.tooltipLabelElement.nativeElement.getBoundingClientRect();
    const isClippedVertical = sourceElementRect.top < 0 && sourceElementRect.bottom > 0;
    const isClippedHorizontal = sourceElementRect.left < 0 && sourceElementRect.right > 0;
    return isClippedVertical || isClippedHorizontal;
  }
  private get _isClick() {
    return this.trigger !== "custom" && ["click"].includes(this.trigger);
  }
  private get _isHover() {
    return this.trigger !== "custom" && ["auto", "hover"].includes(this.trigger);
  }
  private get _isFocus() {
    return this.trigger !== "custom" && ["auto", "focus"].includes(this.trigger);
  }

  ngAfterViewInit() {
    // Set title text to projected content unless a title has already been defined
    if (this.tooltipLabelDirective) this.label = this.tooltipLabelDirective.elementRef.nativeElement.textContent ?? "";
    this.setDocument();
    // We need to place this last in call stack to ensure view is fully rendered
    // if not the tooltip might not be correctly positioned
    setTimeout(this.setTooltipPosition);
  }

  ngOnDestroy() {
    if (this.document.body.contains(this.tooltipElement.nativeElement))
      this.renderer.removeChild(this.document.body, this.tooltipElement.nativeElement);
  }

  /** Set body position and append tooltip as child */
  private setDocument() {
    // Body element needs to be relative to correctly position tooltip element using absolute positioning
    this.renderer.setStyle(this.document.body, "position", "relative");
    // Attaching tooltip element as child to body to ensure it will not be clipped in case overflow hidden is used
    this.renderer.appendChild(this.document.body, this.tooltipElement.nativeElement);
  }

  /** Sets the position of the tooltip label and the arrow */
  private setTooltipPosition = () => {
    // No need to recalculate position if the tooltip is hidden
    if (!this.open) return;
    const contexts = this.getContexts();
    // We are adding the label here to ensure that we get the width when the
    // inner text has been fully rendered
    this.tooltipLabelElement.nativeElement.innerText = this.label;
    this.setElementPosition(this.tooltipLabelElement.nativeElement, contexts[this.position], 15);
    this.setElementPosition(this.tooltipArrowElement.nativeElement, contexts[this.position], 10);
    // If the tooltip doesn't fit into the specified position we will move it to
    // a better suited position where it will appear in view
    if (this._isClipped) {
      const context = this.getContextBasedOnScreenEstate(contexts);
      this.setElementPosition(this.tooltipLabelElement.nativeElement, context, 15);
      this.setElementPosition(this.tooltipArrowElement.nativeElement, context, 10);
    }
    // We need to put the rotation here otherwise the calculation
    // will not be based on the real dimensions and the arrow will be off center
    this.renderer.setStyle(this.tooltipArrowElement.nativeElement, "transform", "rotate(45deg)");
  };

  /** Returns a hash object with positioning instructions based on the current position (of the tooltip element) */
  private getContexts(): TooltipContexts {
    const sourceElementRect = this.elementRef.nativeElement.getBoundingClientRect();
    const bodyElementRect = this.document.body.getBoundingClientRect();

    const xCenterPosition = sourceElementRect.left + sourceElementRect.width / 2;
    const yCenterPosition = sourceElementRect.top - bodyElementRect.top + sourceElementRect.height / 2;

    return {
      top: {
        availableScreenEstate: sourceElementRect.top,
        anchorPosition: "bottom",
        xPosition: ["left", xCenterPosition],
        yPosition: ["bottom", bodyElementRect.bottom - sourceElementRect.top],
      },
      right: {
        availableScreenEstate: window.innerWidth - sourceElementRect.right,
        anchorPosition: "left",
        xPosition: ["left", sourceElementRect.right],
        yPosition: ["top", yCenterPosition],
      },
      bottom: {
        availableScreenEstate: window.innerHeight - sourceElementRect.bottom,
        anchorPosition: "top",
        xPosition: ["left", xCenterPosition],
        yPosition: ["top", sourceElementRect.bottom - bodyElementRect.top],
      },
      left: {
        availableScreenEstate: sourceElementRect.left,
        anchorPosition: "right",
        xPosition: ["right", bodyElementRect.width - sourceElementRect.left],
        yPosition: ["top", yCenterPosition],
      },
    };
  }

  /** Sets open state and dispatch event */
  private setOpen(open: boolean) {
    this.open = open;
    this.openChange.emit(open);
  }

  /** Returns the context for the position (top, right, etc.) with the most available screen estate */
  private getContextBasedOnScreenEstate(contexts: TooltipContexts) {
    return Object.values(contexts).reduce((context, currentContext) =>
      currentContext.availableScreenEstate > context?.availableScreenEstate ?? 0 ? currentContext : context
    );
  }

  /** Resets the position style properties to auto */
  private resetElementPositionProperties(sourceElement: HTMLElement) {
    ["top", "right", "bottom", "left", "transform"].forEach((position) => this.renderer.removeStyle(sourceElement, position));
  }

  /** Sets source element position according to the provided context */
  private setElementPosition(sourceElement: HTMLElement, context: TooltipContext, margin: number) {
    this.resetElementPositionProperties(sourceElement);

    const bodyElementRect = this.document.body.getBoundingClientRect();
    const sourceElementRect = sourceElement.getBoundingClientRect();

    const isSourceElementWidthExceedingBody = bodyElementRect.width < sourceElementRect.width + margin * 2;

    if (isSourceElementWidthExceedingBody) {
      // Set width of source element to the width of body
      sourceElementRect.width = bodyElementRect.width - margin * 2;
      this.renderer.setStyle(sourceElement, "width", toPx(sourceElementRect.width));
    }

    const sourceElementHeight = sourceElementRect.height / 2;
    const sourceElementWidth = sourceElementRect.width / 2;

    const yPositionCenter = context.yPosition[1] - sourceElementHeight;
    const xPositionCenter = context.xPosition[1] - sourceElementWidth;

    // If the tooltip is bleeding outside of the viewport we have to shift in
    // the relevant direction until its completely visible in the view
    const offsetPosition = {
      top: Math.min(yPositionCenter - margin, 0) / -1,
      right: Math.max(margin + sourceElementWidth + context.xPosition[1] - bodyElementRect.width, 0) / -1,
      left: Math.min(xPositionCenter - margin, 0) / -1,
      bottom: Math.max(margin + sourceElementHeight + context.yPosition[1] - bodyElementRect.height, 0) / -1,
    };

    const xPosition = offsetPosition.left + xPositionCenter + offsetPosition.right;
    const yPosition = offsetPosition.top + yPositionCenter + offsetPosition.bottom;

    switch (context.anchorPosition) {
      case "top":
      case "bottom": {
        // Center horizontally
        this.renderer.setStyle(sourceElement, context.xPosition[0], toPx(xPosition));
        // Add margin
        this.renderer.setStyle(sourceElement, context.yPosition[0], toPx(context.yPosition[1] + margin));
        break;
      }
      case "right":
      case "left": {
        // Center vertically
        this.renderer.setStyle(sourceElement, context.yPosition[0], toPx(yPosition));
        // Add margin
        this.renderer.setStyle(sourceElement, context.xPosition[0], toPx(context.xPosition[1] + margin));
        break;
      }
    }
  }
}
